/* eslint-disable unicorn/filename-case */
import { generateURLWithFilters } from 'services/certificate';
import { getAPI } from 'requests/api';
import { getReportDownload, getReportStatus } from 'services/report';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotification } from '@refinedev/core';

const POLL_INTERVAL_MS = 3000;
// Um relatório que nunca chega a processed nem a error deixaria o botão travado
// para sempre, inclusive depois de recarregar a página — o id fica no localStorage.
const MAX_POLLS = 200;

interface GenerateParams {
  endpoint: string;
  kind: string;
  filters: Record<string, string>;
  successMessage: string;
}

/**
 * Drives the async certificate reports: asks the API to start one, remembers it
 * across reloads and polls until the file is ready, then downloads it.
 *
 * `kind` tells apart reports started from the same page — the free-course listing
 * offers both a ZIP and a single print PDF — so only the button that was clicked
 * shows a loading state. Pages with a single report can pass any constant.
 */
export const useCertificateReport = (storageKey: string) => {
  const kindStorageKey = `${storageKey}_kind`;
  const notification = useNotification();

  const [reportId, setReportId] = useState(
    localStorage.getItem(storageKey) || ''
  );
  const [runningKind, setRunningKind] = useState(
    localStorage.getItem(kindStorageKey) || ''
  );
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollsRef = useRef(0);
  // useNotification devolve um objeto novo a cada render, e o polling depende dele
  // por tabela; sem a ref, o intervalo era destruído e recriado a cada render e um
  // clique a cada menos de 3s adiava a verificação indefinidamente.
  const pollRef = useRef<(() => void) | null>(null);

  const track = useCallback(
    (id: string, kind: string) => {
      setReportId(id);
      setRunningKind(kind);
      localStorage.setItem(storageKey, id);
      localStorage.setItem(kindStorageKey, kind);
    },
    [storageKey, kindStorageKey]
  );

  const notifyFailure = useCallback(() => {
    track('', '');
    notification.open?.({
      type: 'error',
      message: 'Erro no Download',
      description: 'Erro ao baixar os certificados.'
    });
  }, [notification, track]);

  const generate = useCallback(
    async ({ endpoint, kind, filters, successMessage }: GenerateParams) => {
      try {
        const response = await getAPI().get(
          generateURLWithFilters<Record<string, string>>(filters, endpoint)
        );

        track(response.data.id, kind);

        notification.open?.({ type: 'success', message: successMessage });
      } catch {
        notifyFailure();
      }
    },
    [notification, track, notifyFailure]
  );

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const pollOnce = useCallback(async () => {
    pollsRef.current += 1;

    if (pollsRef.current > MAX_POLLS) {
      stopPolling();
      track('', '');
      notification.open?.({
        type: 'error',
        message: 'Download demorou demais',
        description:
          'O arquivo não ficou pronto a tempo. Tente de novo ou estreite o filtro.'
      });

      return;
    }

    try {
      const report = await getReportStatus(reportId);

      if (report.status === 'processed') {
        const documentUrl = await getReportDownload(
          report.files?.[0]?.id || ''
        );

        documentUrl.url && window.open(documentUrl.url, '_self');

        track('', '');
        stopPolling();
      } else if (report.status === 'error') {
        stopPolling();
        notifyFailure();
      }
    } catch {
      stopPolling();
      notifyFailure();
    }
  }, [reportId, track, stopPolling, notifyFailure, notification]);

  useEffect(() => {
    pollRef.current = pollOnce;
  }, [pollOnce]);

  useEffect(() => {
    if (!reportId) {
      return;
    }

    pollsRef.current = 0;
    stopPolling();
    intervalRef.current = setInterval(
      () => pollRef.current?.(),
      POLL_INTERVAL_MS
    );

    return stopPolling;
    // Só o id reinicia o cronômetro; a função de poll entra pela ref acima.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  return { reportId, runningKind, generate };
};
